import React, { useState } from 'react';
import { Helmet } from 'react-helmet'; // SEO Added
import { Calendar, User, ArrowRight, Clock, Tag, Search } from 'lucide-react';

const Blogs = () => {
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = ["All", "Interior Trends", "Architecture", "Lifestyle", "Renovation"];

  // --- BLOG DATA ---
  const posts = [
    {
      id: 1,
      title: "The Return of Art Deco: 2024's Hottest Trend",
      category: "Interior Trends",
      date: "Oct 12, 2023",
      readTime: "5 min read",
      image: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80&w=800",
      excerpt: "Geometric patterns, bold colors, and metallic finishes are making a grand comeback. Here's how to incorporate them subtly."
    },
    {
      id: 2,
      title: "Sustainable Luxury: Eco-Friendly Materials",
      category: "Architecture",
      date: "Sep 28, 2023",
      readTime: "8 min read",
      image: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=800",
      excerpt: "Luxury doesn't have to cost the earth. Discover the new wave of sustainable materials that look and feel premium."
    },
    {
      id: 3,
      title: "Creating a Sanctuary: The Master Bedroom Guide",
      category: "Lifestyle",
      date: "Sep 15, 2023",
      readTime: "6 min read",
      image: "https://images.unsplash.com/photo-1616137466211-f939a420be84?auto=format&fit=crop&q=80&w=800",
      excerpt: "Your bedroom should be your escape. Learn the secrets of lighting and textiles to create a calming retreat."
    },
    {
      id: 4,
      title: "Open Concept vs. Traditional Layouts",
      category: "Renovation",
      date: "Aug 30, 2023",
      readTime: "7 min read",
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=800",
      excerpt: "Debating between knocking down walls or keeping them? We analyze the pros and cons for modern families."
    },
    {
      id: 5,
      title: "Smart Homes: Integrating Tech with Style",
      category: "Interior Trends",
      date: "Aug 10, 2023",
      readTime: "4 min read",
      image: "https://www.ultra-vision.net/wp-content/uploads/2022/06/AdobeStock_328780060-1.png",
      excerpt: "Automation doesn't mean ugly gadgets everywhere. See how we hide tech to keep your aesthetics clean."
    },
    {
      id: 6,
      title: "The Art of Lighting: More Than Just Bulbs",
      category: "Lifestyle",
      date: "Jul 22, 2023",
      readTime: "5 min read",
      image: "https://images.adsttc.com/media/images/5db6/d87f/3312/fdea/7a00/041d/slideshow/led-2-light-recessed-adjustable.jpg?1572264036",
      excerpt: "Lighting can make or break a room. Understand the three layers of lighting every luxury home needs."
    }
  ];

  const filteredPosts = activeCategory === "All" ? posts : posts.filter(post => post.category === activeCategory);

  // SEO Schema for Blog Page
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Luxuriya Homes Design Journal",
    "description": "Insights on luxury interior design, architecture trends, and home renovation in Delhi NCR.",
    "publisher": {
      "@type": "Organization",
      "name": "Luxuriya Homes",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.luxuriyahomes.com/logo.png"
      }
    },
    "blogPost": posts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "image": post.image,
      "datePublished": post.date,
      "articleSection": post.category
    }))
  };

  return (
    <div className="bg-zinc-50 min-h-screen text-zinc-900 font-sans selection:bg-amber-200 selection:text-black">
      
      {/* --- SEO HEAD --- */}
      <Helmet>
        <title>Interior Design Blog & Trends | Luxuriya Homes Delhi</title>
        <meta name="description" content="Read the latest luxury interior design trends, architecture insights, and renovation tips for homes in Delhi, Noida & Lucknow." />
        <meta name="keywords" content="interior design blog india, luxury home trends 2025, architecture blog delhi, home renovation tips, smart home ideas" />
        <link rel="canonical" href="https://www.luxuriyahomes.com/blogs" />
        <script type="application/ld+json">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      {/* ================= 1. HERO SECTION ================= */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden bg-zinc-900">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=2000&auto=format&fit=crop" 
            alt="Library Luxury Interior" 
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-black/50"></div>
        </div>

        <div className="relative z-10 text-center px-4 pt-16">
          <p className="text-amber-400 tracking-[0.3em] uppercase text-xs font-bold mb-4">Insights & Inspiration</p>
          <h1 className="text-5xl md:text-7xl font-serif text-white mb-6 drop-shadow-xl">The Design Journal</h1>
          <p className="text-zinc-300 max-w-2xl mx-auto text-lg font-light">
            Curated articles on architecture, interior trends, and the art of luxury living in Delhi & UP.
          </p>
        </div>
      </section>

      {/* ... (Rest of the component remains same) ... */}
      {/* Copy the rest of your Filter, Featured Article, Grid, and Newsletter sections here */}
      
      {/* ================= 2. FEATURED ARTICLE ================= */}
      <section className="max-w-7xl mx-auto px-4 -mt-20 relative z-20 mb-20">
        <div className="bg-white rounded-xl overflow-hidden shadow-2xl flex flex-col lg:flex-row border border-zinc-100 group">
          
          {/* Image */}
          <div className="lg:w-3/5 h-[400px] lg:h-[500px] relative overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80&w=1200" 
              alt="Featured Blog - Quiet Luxury Trend" 
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute top-6 left-6 bg-amber-600 text-white px-4 py-2 text-xs font-bold uppercase tracking-widest">
              Editor's Pick
            </div>
          </div>

          {/* Content */}
          <div className="lg:w-2/5 p-10 lg:p-14 flex flex-col justify-center">
            <div className="flex items-center gap-4 text-xs text-zinc-500 mb-4 uppercase tracking-wider font-semibold">
              <span className="text-amber-600">Interior Trends</span>
              <span>•</span>
              <span>Nov 10, 2023</span>
            </div>
            
            <h2 className="text-3xl lg:text-4xl font-serif text-zinc-900 mb-6 leading-tight group-hover:text-amber-700 transition-colors">
              "Quiet Luxury": Why Minimalism is Getting a Warm Makeover
            </h2>
            
            <p className="text-zinc-500 mb-8 leading-relaxed">
              Gone are the days of sterile white boxes. The new wave of minimalism embraces warm textures, natural stones, and a sense of coziness without the clutter.
            </p>

            <button className="flex items-center gap-2 text-black text-sm font-bold uppercase tracking-widest hover:text-amber-600 transition-colors group/btn">
              Read Full Article <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform"/>
            </button>
          </div>

        </div>
      </section>

      {/* ================= 3. FILTER & SEARCH ================= */}
      <section className="max-w-7xl mx-auto px-4 mb-12">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 border-b border-zinc-200 pb-6">
          <div className="flex flex-wrap gap-2 justify-center md:justify-start">
             {categories.map((cat) => (
               <button
                 key={cat}
                 onClick={() => setActiveCategory(cat)}
                 className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                   activeCategory === cat 
                   ? 'bg-black text-white' 
                   : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                 }`}
               >
                 {cat}
               </button>
             ))}
          </div>
          <div className="relative w-full md:w-64">
             <input 
               type="text" 
               placeholder="Search articles..." 
               className="w-full bg-white border border-zinc-300 rounded-full py-2 pl-4 pr-10 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 text-sm"
             />
             <Search size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400" />
          </div>
        </div>
      </section>

      {/* ================= 4. BLOG GRID ================= */}
      <section className="max-w-7xl mx-auto px-4 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
           {filteredPosts.map((post) => (
             <article key={post.id} className="bg-white rounded-lg overflow-hidden border border-zinc-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group cursor-pointer flex flex-col h-full">
                <div className="h-60 overflow-hidden relative">
                   <img 
                     src={post.image} 
                     alt={post.title} 
                     className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                   />
                   <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 text-xs font-bold uppercase tracking-wider rounded-sm shadow-sm">
                      {post.category}
                   </div>
                </div>
                <div className="p-6 flex flex-col flex-grow">
                   <div className="flex items-center gap-4 text-xs text-zinc-400 mb-3">
                      <span className="flex items-center gap-1"><Calendar size={12}/> {post.date}</span>
                      <span className="flex items-center gap-1"><Clock size={12}/> {post.readTime}</span>
                   </div>
                   <h3 className="text-xl font-serif font-bold text-zinc-900 mb-3 leading-snug group-hover:text-amber-600 transition-colors">
                     {post.title}
                   </h3>
                   <p className="text-zinc-500 text-sm leading-relaxed mb-6 line-clamp-3">
                     {post.excerpt}
                   </p>
                   <div className="mt-auto pt-4 border-t border-zinc-100 flex justify-between items-center">
                      <span className="text-xs font-bold uppercase tracking-widest text-zinc-900 group-hover:text-amber-600 transition-colors">Read More</span>
                      <ArrowRight size={16} className="text-zinc-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-transform"/>
                   </div>
                </div>
             </article>
           ))}
        </div>
        <div className="text-center mt-16">
          <button className="px-8 py-3 border border-zinc-300 text-zinc-600 uppercase tracking-widest text-xs font-bold hover:bg-black hover:text-white hover:border-black transition-all">
            Load More Articles
          </button>
        </div>
      </section>

      {/* ================= 5. NEWSLETTER CTA ================= */}
      <section className="bg-zinc-900 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
           <div className="w-16 h-16 bg-amber-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Tag size={32} className="text-amber-500" />
           </div>
           <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">Subscribe to Luxury Living</h2>
           <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
             Get the latest design trends, exclusive home tours, and architectural insights delivered directly to your inbox.
           </p>
           <form className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
              <input 
                type="email" 
                placeholder="Your email address" 
                className="flex-1 bg-white/10 border border-white/20 text-white placeholder-zinc-500 px-6 py-4 rounded-sm focus:outline-none focus:border-amber-500 transition-colors"
              />
              <button className="bg-amber-500 text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-white transition-colors rounded-sm">
                Subscribe
              </button>
           </form>
           <p className="text-zinc-600 text-xs mt-4">We respect your privacy. Unsubscribe at any time.</p>
        </div>
      </section>

    </div>
  );
};

export default Blogs;